<template>

  <v-container fluid class="mb-6 page-content provider-order-list-table" v-if="connectionInfo">

    <!----------------------------->
    <!-- Page Label and Controls -->
    <!----------------------------->
    <v-container fluid class="page-header-area">
      <v-row align="start">
        <v-col cols="12" xs="12" sm="8" class="pa-0 ma-0 pl-2 mt-2">
          <!-- (page label, refresh data action) -->
          <div class="page-label-area">
            <span class="page-label">{{ connectionInfo.name }}</span>
            <v-btn text color="primary" class="ml-2 mb-2" @click="loadData">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" xs="12" sm="4" class="pa-0 ma-0 pl-2 mt-2">
          <div style="color:#177e9e;font-size:1.2em;">
            {{ formatProvider(connectionInfo.provider_alias) }}
          </div>
          <div style="font-size:1.08em;" v-if="connectionInfo.account">
            {{ connectionInfo.account.name }}
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!---------------->
    <!-- Order List -->
    <!---------------->
    <div class="mt-8">
      <v-data-table
          class="mt-6"
          :headers="headers"
          :items="orderItems"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems">

        <!-- order number -->
        <template v-slot:item.thirdparty_order_number="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  text
                  color="primary"
                  class="white--text"
                  v-bind="attrs"
                  v-on="on"
                  @click="openProviderOrderDetailsPreview(item)">
                <v-icon small class="mr-2">mdi-magnify</v-icon>
                <span style="font-size:1.0rem;">{{ item.thirdparty_order_number }}</span>
              </v-btn>
            </template>
            <div>Preview order details</div>
          </v-tooltip>
        </template>

        <!-- shipping to -->
        <template v-slot:item.receiver_info="{ item }">
          <span style="font-size:1.05em">{{ formatShippingTo(item.receiver_info) }}</span>
        </template>

        <!-- expected delivery date -->
        <template v-slot:item.expected_delivery_date="{ item }">
          <span>{{ formatDate(item.expected_delivery_date) }}</span>
        </template>

        <!-- order total -->
        <template v-slot:item.order_details="{ item }">
          <span style="font-size:1.1em">{{ formatTotal(item.order_details) }}</span>
        </template>

        <!-- provider status -->
        <template v-slot:item.thirdparty_status="{ item }">
          <span style="font-style:italic">{{ item.thirdparty_status }}</span>
        </template>

        <!-- ship with RouteLeaf action (or credit memo) -->
        <template v-slot:item.order_status="{ item }">
          <div v-if="item.order_status === 'SUBMITTED'" class="submitted-status-text">
            Submitted to {{ appName }}
          </div>
          <router-link :to="selectForCredit(item)" v-else-if="item.order_details && item.order_details.total < 0">
            <v-btn
                color="yellow darken-4"
                class="action-button"
                :disabled="!isReadyToShip(item.order_status)">
              {{ issueCreditText }}
            </v-btn>
          </router-link>
          <router-link :to="selectForDelivery(item)" v-else>
            <v-btn
                color="blue"
                class="action-button"
                :disabled="!isReadyToShip(item.order_status)">
              {{ shipButtonText }}
            </v-btn>
          </router-link>
        </template>
      </v-data-table>
    </div>

    <!----------------------------------->
    <!-- Provider Order Details Dialog -->
    <!----------------------------------->
    <v-dialog persistent max-width="850" class="provider-order-preview-dialog" v-model="isProviderOrderDetailsDialogOpen">
      <v-card class="provider-order-details-preview">
        <v-card-text class="pb-1">
          <provider-order-details-preview
              ref="provider-order-preview"
              :orderId="orderIdToView"
              :orderNumber="orderNumberToView"
              :providerAlias="providerAliasToView"
              :shipper="shipper"
              :accountId="accountIdToView"
              :brandId="brandIdToView"
              :brandName="brandNameToView"
              v-on:close="closeProviderOrderDetailsDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import ProviderOrderDetailsPreview from '@/components/providerOrders/ProviderOrderDetailsPreview'

export default {
  name: 'ProviderOrderListDataTable',

  components: {
    ProviderOrderDetailsPreview
  },

  props: {
    headers: {
      type: Array,
      default: () => [
        { text: 'Order #', sortable: false, value: 'thirdparty_order_number', width: '180px' },
        { text: 'Total', sortable: false, value: 'order_details' },
        { text: 'Shipping To', sortable: false, value: 'receiver_info', width: '27%' },
        { text: 'Expected Delivery', sortable: false, value: 'expected_delivery_date', align: 'center' },
        { text: 'Provider Status', sortable: false, value: 'thirdparty_status' },
        { text: 'Ship with RouteLeaf', sortable: false, value: 'order_status' }
      ]
    },
    connectionInfo: {
      type: Object,
      required: true
    },
    defaultSortField: {
      type: String,
      default: null
    },
    defaultSortOrder: {
      type: String,
      default: 'dsc' // dsc = descending, asc = ascending
    }
  },

  data () {
    return {
      isDataLoading: false,
      isPageLoading: false,
      orderData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50]
      },
      dataOptions: {
        itemsPerPage: 10
      },
      // ---------- Provider order details preview
      isProviderOrderDetailsDialogOpen: false,
      orderIdToView: null,
      orderNumberToView: null,
      providerAliasToView: null,
      accountIdToView: null,
      brandIdToView: null,
      brandNameToView: null
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    // Refresh text
    refreshButtonText () {
      return 'Refresh'
    },
    // Ship Order action
    shipButtonText () {
      return 'Ship Order'
    },
    // Issue Credit action
    issueCreditText () {
      return 'Issue Credit'
    },
    orderItems () {
      return (this.orderData) ? this.orderData.items : []
    },
    dataMeta () {
      return (this.orderData) ? this.orderData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    },
    shipper () {
      return this.connectionInfo.shipper || {}
    }
  },

  watch: {
    dataOptions: {
      handler () {
        this.loadData()
      },
      deep: true
    }
  },

  beforeDestroy () {
    this.clearProviderOrderDetailsPreview()
  },

  methods: {
    isReadyToShip (status) {
      return status !== 'SUBMITTED' && this.shipper.shipper_status === 'APPROVED'
    },

    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MMMM D') : '-'
    },

    formatTime (time) {
      return (time) ? this.$root.utils.format(time, 'h:mm a') : '-'
    },

    formatShippingTo (receiverInfo = {}) {
      const location = receiverInfo.location || {}
      const locLabel = location.name || ''
      return locLabel
    },

    formatTotal (orderDetails = {}) {
      const total = orderDetails.total || 0
      return total.toLocaleString(undefined, { minimumFractionDigits: 2 })
    },

    // Display provider name
    formatProvider (alias) {
      return (alias) ? this.$root.providerAliasToName(alias) : ''
    },

    clearProviderOrderDetailsPreview () {
      this.orderIdToView = null
      this.orderNumberToView = null
      this.providerAliasToView = null
      this.accountIdToView = null
      this.brandIdToView = null
      this.brandNameToView = null
    },

    openProviderOrderDetailsPreview (item) {
      if (item) {
        // Set item details to preview and open dialog
        this.orderIdToView = item.thirdparty_order_id
        this.orderNumberToView = item.thirdparty_order_number
        this.providerAliasToView = this.connectionInfo.provider_alias
        this.accountIdToView = this.connectionInfo.account_id
        this.brandIdToView = this.connectionInfo.brand_id
        this.brandNameToView = this.connectionInfo.name
        this.isProviderOrderDetailsDialogOpen = true
      }
    },

    closeProviderOrderDetailsDialog () {
      this.isProviderOrderDetailsDialogOpen = false
      this.clearProviderOrderDetailsPreview()
    },

    selectForCredit (item) {
      if (item && this.isReadyToShip(item.order_status)) {
        return {
          name: 'provider-order-issue-credit',
          params: {
            orderId: item.thirdparty_order_id,
            providerAlias: this.connectionInfo.provider_alias,
            accountId: this.connectionInfo.account_id,
            brandId: this.connectionInfo.brand_id
          }
        }
      } else {
        return ''
      }
    },

    selectForDelivery (item) {
      if (item && this.isReadyToShip(item.order_status)) {
        return {
          name: 'provider-order-submit',
          params: {
            orderId: item.thirdparty_order_id,
            providerAlias: this.connectionInfo.provider_alias,
            accountId: this.connectionInfo.account_id,
            brandId: this.connectionInfo.brand_id
          }
        }
      } else {
        return ''
      }
    },

    // Generate link data for order info page
    generateOrderInfoPageLink (item) {
      if (item && item.model) {
        return {
          name: 'receiver-order-info-summary',
          params: { orderId: item.id }
        }
      } else {
        return ''
      }
    },

    async loadData () {
      if (!this.connectionInfo) return false
      this.isDataLoading = true

      try {
        // Load orders for key, if active
        const results = await this.performGetData()
        this.orderData = results
      } catch (err) {
        // Show system down message
        if (err.status && err.status === 503) {
          this.$root.showSystemDownMessage()
        } else {
          console.log('Error loading provider orders =>', err)
        }
      }

      this.isDataLoading = false
    },

    async performGetData () {
      let results = null

      const page = this.dataOptions.page || 1
      const limit = this.dataOptions.itemsPerPage
      const skip = (page === 1) ? 0 : (page - 1) * limit

      const providerAlias = this.connectionInfo.provider_alias
      const status = this.connectionInfo.status
      const accountId = this.connectionInfo.account_id
      const brandId = this.connectionInfo.brand_id
      const enabledStatuses = (this.connectionInfo.settings)
        ? this.connectionInfo.settings.status
        : null

      // Build request params
      const params = {
        provider: providerAlias,
        account_id: accountId,
        brand_id: brandId,
        skip,
        limit
      }
      if (enabledStatuses) params.status = enabledStatuses.join()

      // Perform request if key is active
      if (status === 'ACTIVE') {
        results = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'provider/orders',
          opts: { params }
        })
      }

      return results
    }
  }
}
</script>

<style>
  .provider-order-list-table .submitted-status-text {
    border: 1px solid #727272;
    border-radius: 4px;
    padding-top: 4px;
    padding-bottom: 4px;
    text-align: center;
    color: #727272;
    font-style: italic;
    letter-spacing: 1px;
    width: 140px;
  }

  .provider-order-list-table .action-button {
    height: 52px !important;
    width: 140px;
  }

  .provider-order-list-table .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .provider-order-list-table .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .provider-order-list-table .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 75px;
  }

  .provider-order-list-table .v-data-table a {
    text-decoration: none;
  }
  .provider-order-list-table .v-data-table a:hover {
    text-decoration: underline;
  }

  .provider-order-details-preview {
    background-color: #121212 !important;
  }
</style>
